import { createStyles, lighten, makeStyles, Theme, Tooltip } from '@material-ui/core';
import { Edit, ReportProblemOutlined } from '@material-ui/icons';
import { RenderRichText } from '@talentnow/shared-ui/components';
import moment from 'moment';
import React, { useMemo } from 'react';
import TnTypography from '../../../../_components/tn-typography/TnTypography';

import EmploymentForm, { Employment } from './EmploymentForm';

interface EmploymentHandlers {
  onEditEmployment: (employment: Employment) => void;
  onDeleteEmployment: () => void;
}

interface Props {
  employment: Employment;
  changeHandlers: EmploymentHandlers;
  onFormOpenChange?: (isOpen: boolean) => void;
  onFormValidityChange?: (isValid: boolean, invalidFields: string[]) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    labelContainer: {
      width: '100%',
      marginBottom: 16,
    },
    addEmploymentButton: {
      width: 200,
    },
    employment: {
      width: '100%',
      justifyContent: 'space-between',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      '&:not(:last-child)': {
        borderBottom: `1px solid #E0E0E0`,
        paddingBottom: 8,
      },
    },
    headerContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      width: '100%',
      alignItems: 'center',
    },
    headerTextSection: {
      display: 'flex',
      flexDirection: 'column',
    },
    headerButtonSection: {
      display: 'flex',
      flexDirection: 'row',
      gap: 8,
      alignItems: 'center',
    },
    editIconContainer: {
      cursor: 'pointer',
      border: `1px solid ${lighten(theme.palette.primary.main, 0.5)}`,
      padding: '4px 4px 0px 4px',
      borderRadius: 4,
    },
    editIcon: {
      fill: theme.palette.primary.main,
    },
  }),
);

export default function EmploymentPosition({
  employment,
  changeHandlers,
  onFormOpenChange,
  onFormValidityChange,
}: Readonly<Props>) {
  const classes = useStyles();

  const [editFormOpen, setEditFormOpen] = React.useState(false);

  const formIsInvalid = useMemo(() => {
    return (
      !employment.companyName ||
      !employment.jobTitle ||
      !employment.startDate ||
      (employment.endDate && moment(employment.endDate).isBefore(moment(employment.startDate)))
    );
  }, [employment]);

  return (
    <div className={classes.employment}>
      <div className={classes.headerContainer}>
        <div className={classes.headerTextSection}>
          <TnTypography bold variant="caption">
            {employment.companyName} &#x2022; {employment.jobTitle}
          </TnTypography>
          <TnTypography color="lightGrey" variant="caption2">
            {moment(employment.startDate).format('MMM YYYY')} -{' '}
            {employment.endDate ? moment(employment.endDate).format('MMM YYYY') : 'Present'}
          </TnTypography>
        </div>
        <div className={classes.headerButtonSection}>
          {formIsInvalid && (
            <Tooltip title="Missing required fields or invalid date range" placement="top">
              <div style={{ color: '#F57C00' }}>
                <ReportProblemOutlined />
              </div>
            </Tooltip>
          )}
          <div className={classes.editIconContainer}>
            <Edit
              className={classes.editIcon}
              onClick={() => {
                const nextState = !editFormOpen;
                setEditFormOpen(nextState);
                onFormOpenChange?.(nextState);
              }}
            />
          </div>
        </div>
      </div>
      {employment.additionalInfo && (
        <div style={{ marginTop: -8 }}>
          <RenderRichText maxLines={2} smallFont richTextValue={employment.additionalInfo} />
        </div>
      )}

      {editFormOpen && (
        <div style={{ marginTop: 8 }}>
          <EmploymentForm
            mode="edit"
            onDelete={() => {
              changeHandlers.onDeleteEmployment();
              onFormOpenChange?.(false);
            }}
            onSubmit={(updatedEmployment) => {
              changeHandlers.onEditEmployment(updatedEmployment);
              setEditFormOpen(false);
              onFormOpenChange?.(false);
            }}
            onCancel={() => {
              setEditFormOpen(false);
              onFormOpenChange?.(false);
            }}
            employment={employment}
            onValidityChange={onFormValidityChange}
          />
        </div>
      )}
    </div>
  );
}
